import React from 'react'
import { PageShell } from './PageShell'
import { LogVisitModal } from '../components/LogVisitModal'
import { useDashboard } from '../DashboardContext'
import { relativeTimeLabel, initialsFromName } from '../utils'
import { SkeletonTableRows } from '../../../components/ui/Skeleton'
import type { NudgeQueueSummary } from '../../../types/api'
import type { NudgeStatus } from '../../../types/enums'

// Statuses where there is no point continuing to nudge: the row has either
// concluded (RECOVERED/RETURNED/NO_SHOW/OPT_OUT) or given up (EXHAUSTED/FAILED).
const TERMINAL: NudgeStatus[] = [
  'RECOVERED', 'RETURNED', 'NO_SHOW', 'OPT_OUT', 'EXHAUSTED', 'FAILED',
]

// Most-recently-classified intent → owner-facing label + pill modifier.
// INTERESTED is the highest-signal "warming up" — show it first; UNCLEAR is the lowest.
const INTENT_LABELS: Record<string, { label: string; pill: string; priority: number }> = {
  INTERESTED:            { label: 'Showing interest',    pill: 'pill-recovered', priority: 1 },
  RELATIONSHIP_QUESTION: { label: 'Asking about history', pill: 'pill-pending',   priority: 2 },
  QUESTION:              { label: 'Asking a question',    pill: 'pill-pending',   priority: 3 },
  UNCLEAR:               { label: 'Unclear reply',        pill: 'pill-pending',   priority: 4 },
}

export function EngagingPage({ isActive }: { isActive: boolean }) {
  const { queueRows, queueLoading, refreshQueue } = useDashboard()
  const [logVisitTarget, setLogVisitTarget] = React.useState<{
    contactId: string
    contactName: string
  } | null>(null)

  // One row per contact: the contact has replied at least once AND isn't terminal.
  // Pick the most recently updated non-terminal row to represent the contact.
  const engagingRows = React.useMemo(() => {
    const byContact = new Map<string, NudgeQueueSummary>()
    for (const row of queueRows) {
      if (TERMINAL.includes(row.status)) continue
      if (!row.replyReceivedAt) continue
      const existing = byContact.get(row.contactId)
      if (!existing || new Date(row.updatedAt) > new Date(existing.updatedAt)) {
        byContact.set(row.contactId, row)
      }
    }
    return Array.from(byContact.values()).sort((a, b) => {
      // Sort by intent priority first (INTERESTED highest), then recency.
      const ap = INTENT_LABELS[a.lastIntent ?? '']?.priority ?? 99
      const bp = INTENT_LABELS[b.lastIntent ?? '']?.priority ?? 99
      if (ap !== bp) return ap - bp
      return new Date(b.replyReceivedAt ?? b.updatedAt).getTime()
           - new Date(a.replyReceivedAt ?? a.updatedAt).getTime()
    })
  }, [queueRows])

  return (
    <PageShell id="page-engaging" isActive={isActive}>
      <div className="content">
        <div className="lapsed-header-bar">
          <div>
            <div className="lh-num">{engagingRows.length}</div>
            <div
              style={{
                fontSize: 10,
                fontFamily: "'DM Mono',monospace",
                color: 'var(--text3)',
                marginTop: 2,
              }}
            >
              IN CONVERSATION
            </div>
          </div>
          <div className="lh-divider" />
          <div>
            <div className="lh-label">Engaging Contacts</div>
            <div className="lh-sub">
              These customers have replied at least once and may be warming up.
              If one of them comes in, log the visit and we&apos;ll move them straight
              to Returned.
            </div>
          </div>
        </div>

        <div className="panel" aria-busy={queueLoading} aria-label="Engaging contacts">
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Last intent</th>
                <th>Last reply</th>
                <th>Nudge #</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {queueLoading ? (
                <SkeletonTableRows cols={5} rows={5} />
              ) : engagingRows.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8,
                        padding: '48px 16px',
                        color: 'var(--text2)',
                        fontSize: 13,
                      }}
                    >
                      <svg
                        width="36"
                        height="36"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ opacity: 0.35 }}
                        aria-hidden="true"
                      >
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      <span>No contacts currently in conversation.</span>
                    </div>
                  </td>
                </tr>
              ) : (
                engagingRows.map(row => {
                  const meta = INTENT_LABELS[row.lastIntent ?? '']
                  return (
                    <tr key={row.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            className="avatar-circle"
                            style={{ background: 'var(--orange-bg)', color: 'var(--orange)' }}
                          >
                            {initialsFromName(row.contactName)}
                          </div>
                          <div>
                            <div className="cust-name">{row.contactName}</div>
                            <div className="cust-sub">{row.contactEmail}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {meta ? (
                          <span className={`pill ${meta.pill}`}>{meta.label}</span>
                        ) : (
                          <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td
                        style={{
                          fontFamily: "'DM Mono',monospace",
                          fontSize: 12,
                          color: 'var(--text2)',
                        }}
                      >
                        {row.replyReceivedAt ? relativeTimeLabel(row.replyReceivedAt) : '—'}
                      </td>
                      <td>
                        <span className="pill pill-pending">Nudge #{row.sequenceNumber}</span>
                      </td>
                      <td>
                        <button
                          className="btn-primary"
                          type="button"
                          style={{ fontSize: 12, padding: '6px 14px' }}
                          onClick={() =>
                            setLogVisitTarget({
                              contactId: row.contactId,
                              contactName: row.contactName,
                            })
                          }
                          title="The customer came in — log the visit to mark this as Returned"
                        >
                          Log Visit
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <LogVisitModal
        open={logVisitTarget !== null}
        contactId={logVisitTarget?.contactId ?? ''}
        contactName={logVisitTarget?.contactName ?? ''}
        onClose={() => setLogVisitTarget(null)}
        onSaved={() => {
          setLogVisitTarget(null)
          refreshQueue()
        }}
      />
    </PageShell>
  )
}
